import { jsPDF } from "jspdf";

/*
  ReceiptDownload.jsx (updated)

  - Single module exposing:
      - generatePDF(receipt, opts)    -> creates Receipt (student + office copy)
      - generateVoucher(row, opts)   -> creates Expense Voucher (single page)
      - generateAuto(row, opts)      -> chooses Voucher for refunds, Receipt otherwise
  - Keeps robust receipt behavior (refunds shown as Refund line).
  - Adds voucher generator so you don't need a separate VoucherDownload module.
  - File path: src/components/ReceiptDownload.jsx
*/

function safeNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function getClassLabel(courseYear) {
  if (courseYear === null || courseYear === undefined || courseYear === "") return "-";
  const s = String(courseYear).trim();
  if (s === "1" || s === "1st" || /^\s*1\b/.test(s)) return "1st Year GNM";
  if (s === "2" || s === "2nd" || /^\s*2\b/.test(s)) return "2nd Year GNM";
  if (s === "3" || s === "3rd" || /^\s*3\b/.test(s)) return "3rd Year GNM";
  if (/Year/i.test(s)) return s;
  return s;
}

function amountToWordsIN(amount) {
  const num = Number(amount);
  if (!isFinite(num)) return "";

  const n = Math.abs(Math.round(num * 100) / 100);
  const rupees = Math.floor(n);
  const paise = Math.round((n - rupees) * 100);

  const ONES = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen",
    "Sixteen", "Seventeen", "Eighteen", "Nineteen"
  ];
  const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  function twoDigitToWords(x) {
    if (x < 20) return ONES[x];
    const t = Math.floor(x / 10);
    const o = x % 10;
    return TENS[t] + (o ? " " + ONES[o] : "");
  }

  function threeDigitToWords(x) {
    let res = "";
    const h = Math.floor(x / 100);
    const rem = x % 100;
    if (h) {
      res += ONES[h] + " Hundred";
      if (rem) res += " ";
    }
    if (rem) res += twoDigitToWords(rem);
    return res.trim();
  }

  function intToIndianWords(num) {
    if (num === 0) return "Zero";
    let parts = [];
    const crore = Math.floor(num / 10000000);
    if (crore) {
      parts.push(intToIndianWords(crore) + " Crore");
      num = num % 10000000;
    }
    const lakh = Math.floor(num / 100000);
    if (lakh) {
      parts.push(intToIndianWords(lakh) + " Lakh");
      num = num % 100000;
    }
    const thousand = Math.floor(num / 1000);
    if (thousand) {
      parts.push(threeDigitToWords(thousand) + " Thousand");
      num = num % 1000;
    }
    if (num) {
      parts.push(threeDigitToWords(num));
    }
    return parts.join(" ").replace(/\s+/g, " ").trim();
  }

  const rupeesWords = intToIndianWords(rupees);
  if (paise > 0) {
    const paiseWords = intToIndianWords(paise);
    return `${rupeesWords} Rupees and ${paiseWords} Paise Only`;
  } else {
    return `${rupeesWords} Rupees Only`;
  }
}

function formatDateForDisplay(dateInput) {
  if (!dateInput) return "-";
  if (dateInput instanceof Date && !Number.isNaN(dateInput.getTime())) {
    const dd = String(dateInput.getDate()).padStart(2, "0");
    const mm = String(dateInput.getMonth() + 1).padStart(2, "0");
    const yyyy = dateInput.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  const s = String(dateInput).trim();
  if (!s) return "-";
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, yyyy, mm, dd] = isoMatch;
    return `${dd}-${mm}-${yyyy}`;
  }
  const dmyMatch = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (dmyMatch) {
    const [, dd, mm, yyyy] = dmyMatch;
    return `${dd}-${mm}-${yyyy}`;
  }
  const parsed = Date.parse(s);
  if (!Number.isNaN(parsed)) {
    const dt = new Date(parsed);
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yyyy = dt.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  return s;
}

/* Determine whether a row is a refund/voucher row */
function isRefundRow(row) {
  if (!row) return false;
  const rn = String(row.receipt_no || row.voucher_no || "").toUpperCase();
  const acc = String(row.account_head || "").toLowerCase();
  const status = String(row.status || row.payment_status || "").toLowerCase();
  const refundField = row.refund ?? row.refund_amount ?? row.refundAmt ?? row.refunded_amount ?? 0;
  const amt = Number(row.amount ?? row.amount_paid ?? 0);

  if (acc && acc.includes("refund")) return true;
  if (rn && rn.startsWith("RE")) return true;
  if (status && (status === "refunded" || status.includes("refund"))) return true;
  if (!Number.isNaN(Number(refundField)) && Number(refundField) !== 0) return true;
  if (!Number.isNaN(amt) && amt < 0) return true;
  return false;
}

/* ---------- Receipt generation (student + office copy) ---------- */

function getTableRows(receipt) {
  const rows = [];
  const isCarry =
    receipt.mode === "Carryforward" ||
    receipt.mode === "Carryforward from previous year" ||
    (typeof receipt.mode === "string" && receipt.mode.toLowerCase().includes("carryforward"));

  const tuition = safeNumber(
    isCarry ? (receipt.amount || 0) : (receipt.amount_paid ?? receipt.amount ?? 0)
  );
  const scholarship = safeNumber(receipt.scholarship_amount ?? 0);

  const explicitRefund = safeNumber(receipt.refund ?? 0);
  let refund = explicitRefund;
  if (!explicitRefund && receipt.status && String(receipt.status).toLowerCase() === "refunded") {
    refund = Math.abs(safeNumber(receipt.scholarship_amount ?? 0));
  }

  if (isCarry && tuition > 0) {
    rows.push({ sr: 1, desc: "Carryforward Amount", amt: parseFloat(tuition) });
  } else {
    let sr = 1;
    if (tuition > 0) {
      rows.push({ sr: sr++, desc: "Tuition Fee", amt: parseFloat(tuition) });
    }
    if (refund > 0) {
      rows.push({ sr: sr++, desc: "Refund", amt: -Math.abs(parseFloat(refund)) });
    } else if (scholarship > 0) {
      rows.push({ sr: sr++, desc: "Scholarship Fee", amt: parseFloat(scholarship) });
    }
  }

  return rows;
}

function getReceiptNo(receipt) {
  if (!receipt) return "-";
  if (receipt.receipt_no && typeof receipt.receipt_no === "string") {
    const rnos = receipt.receipt_no.split(",").map(r => r.trim()).filter(Boolean);
    return rnos.length === 0 ? "-" : (rnos.length === 1 ? rnos[0] : rnos.join(", "));
  }
  return receipt.receipt_no ?? receipt.receipt_id ?? receipt.id ?? receipt.fee_id ?? "-";
}

function safeText(v, fallback = "-") {
  if (v === null || v === undefined || v === "") return String(fallback);
  return String(v);
}

function drawSingleReceipt(doc, receipt, offsetY, copyType, opts = {}) {
  const leftMargin = 20;
  const rightMargin = 190;
  const usableWidth = rightMargin - leftMargin;
  let y = offsetY + 12;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(0, 0, 0);
  doc.text(safeText("SAROJINI COLLEGE OF NURSING, DHULE"), 105, y, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(safeText("32, Saroday Colony, Nr. New City High School, Dhule - 424001"), 105, y + 5, { align: "center" });
  doc.text(safeText("Email: sarojinicollegeofnursing@gmail.com | Contact: 02562238754, 8275122933, 8275122944."), 105, y + 9, { align: "center" });

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  doc.line(leftMargin, y + 13, rightMargin, y + 13);

  y += 17;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(safeText(`Receipt No: ${getReceiptNo(receipt)}`), leftMargin, y);

  const rawDate = receipt.original_payment_date ?? receipt.payment_date ?? receipt.date ?? null;
  const displayDate = rawDate ? formatDateForDisplay(rawDate) : "-";
  doc.text(safeText(`Date: ${displayDate}`), rightMargin, y, { align: "right" });

  y += 7;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(safeText("Received with thanks from:"), leftMargin, y);

  const studentName = safeText(
    receipt.student_name ??
    receipt.student ??
    receipt.name ??
    (opts && opts.student && (opts.student.name || opts.student.student_name)) ??
    "-"
  );
  doc.setFont("helvetica", "bold");
  doc.text(studentName, leftMargin + 65, y);

  y += 7;
  doc.setFont("helvetica", "normal");
  doc.text(safeText("Payment Mode:"), leftMargin, y);
  doc.setFont("helvetica", "bold");
  const modeText = (receipt.mode === "Carryforward" || receipt.mode === "Carryforward from previous year") ? "Carryforward" : (receipt.mode || "Cash");
  doc.text(safeText(modeText), leftMargin + 34, y);

  doc.setFont("helvetica", "normal");
  doc.text(safeText("Transaction Details:"), leftMargin + 80, y);
  doc.setFont("helvetica", "bold");
  doc.text(safeText(receipt.reference || "-"), leftMargin + 120, y);

  y += 7;
  doc.setFont("helvetica", "bold");
  doc.text(safeText("Batch:"), leftMargin, y);
  doc.setFont("helvetica", "normal");
  const batchLabel = receipt.batch_name ?? (opts && opts.student && opts.student.batch) ?? receipt.batch ?? receipt.batch_id ?? "-";
  doc.text(safeText(batchLabel), leftMargin + 15, y);

  doc.setFont("helvetica", "bold");
  doc.text(safeText("Class:"), leftMargin + 80, y);
  doc.setFont("helvetica", "normal");
  const classLabel = getClassLabel(receipt.course_year ?? (opts && opts.student && opts.student.course_year));
  doc.text(safeText(classLabel), leftMargin + 97, y);

  y += 9;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(safeText("Sr. No."), leftMargin + 3, y);
  doc.text(safeText("Description"), leftMargin + 25, y);
  doc.text(safeText("Amount (Rs.)"), rightMargin - 7, y, { align: "right" });

  const rows = getTableRows(receipt);
  const tableRowsCount = Math.max(rows.length, 3);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.4);
  doc.rect(leftMargin, y - 6, usableWidth, 9 * (tableRowsCount + 1));
  doc.line(leftMargin, y + 2, rightMargin, y + 2);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  let rowY = y + 8;
  rows.forEach(r => {
    doc.text(safeText(r.sr), leftMargin + 3, rowY);
    doc.text(safeText(r.desc), leftMargin + 25, rowY);
    const amt = Number(r.amt || 0);
    const amtStr = amt < 0
      ? "-" + Math.abs(amt).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : Number(amt).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    doc.text(safeText(amtStr), rightMargin - 7, rowY, { align: "right" });
    rowY += 9;
  });

  for (let i = rows.length; i < 3; i++) {
    doc.text(safeText("-"), leftMargin + 3, rowY);
    doc.text(safeText(" "), leftMargin + 25, rowY);
    doc.text(safeText("-"), rightMargin - 7, rowY, { align: "right" });
    rowY += 9;
  }

  rowY += 5;
  const netPaid = rows.reduce((sum, r) => sum + Number(r.amt || 0), 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(safeText("In Words:"), leftMargin, rowY);
  doc.setFont("helvetica", "normal");
  const words = amountToWordsIN(netPaid);
  doc.text(safeText(words ? (words.charAt(0).toUpperCase() + words.slice(1)) : "-"), leftMargin + 22, rowY);

  rowY += 13;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  doc.text(safeText("Subject to Realization"), 105, rowY, { align: "center" });
  rowY += 6;
  doc.setFont("helvetica", "normal");
  doc.text(safeText("This is a system generated receipt and does not require physical signature."), 105, rowY, { align: "center" });

  rowY += 15;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(safeText("Stamp __________________"), leftMargin, rowY);
  doc.text(safeText("Accountant's Signature __________________"), rightMargin - 60, rowY);

  rowY += 8;
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text(safeText(copyType), 105, rowY, { align: "center" });

  if (copyType === "Student Copy") {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(1.2);
    doc.line(leftMargin, rowY + 7, rightMargin, rowY + 7);
  }
}

/* ---------- Voucher generation (single page) ---------- */

function formatINR(v) {
  // Ensure voucher displays a positive value (no leading minus)
  const n = Math.abs(safeNumber(v));
  return "₹" + n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function drawVoucher(doc, row) {
  const left = 20;
  let y = 20;

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Expense Voucher", 105, y, { align: "center" });

  y += 12;
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");

  const labelX = left;
  const valueX = 110;
  const lineHeight = 8;

  const voucherNo = row.voucher_no || row.receipt_no || row.receipt || row.id || "-";
  const rawDate = row.date || row.payment_date || row.created_at || null;
  const dateStr = rawDate ? formatDateForDisplay(rawDate) : "-";
  const account = row.account_head || "Refund/Expense";
  const reference = row.reference || row.transaction_details || "-";
  const mode = row.mode || "-";
  const paidTo = row.student_name || row.name || row.paid_to || "-";
  const amount = Math.abs(safeNumber(row.amount ?? row.refund ?? 0)); // positive
  const status = safeText(row.status || row.payment_status || "-");
  const particulars = safeText(row.particulars || row.particular || row.remarks || (row.refund ? `Refund processed (${voucherNo})` : "-"));
  const remarks = safeText(row.remarks || `Refund processed for ${paidTo} (${voucherNo})`);

  doc.setFont("helvetica", "bold");
  doc.text("Voucher No:", labelX, y);
  doc.setFont("helvetica", "normal");
  doc.text(safeText(voucherNo), valueX, y);
  doc.setFont("helvetica", "bold");
  doc.text("Date:", 160, y);
  doc.setFont("helvetica", "normal");
  doc.text(dateStr, 190, y, { align: "right" });

  y += lineHeight;
  doc.setFont("helvetica", "bold");
  doc.text("Account:", labelX, y);
  doc.setFont("helvetica", "normal");
  doc.text(safeText(account), valueX, y);
  doc.setFont("helvetica", "bold");
  doc.text("Reference:", 160, y);
  doc.setFont("helvetica", "normal");
  doc.text(safeText(reference), 190, y, { align: "right" });

  y += lineHeight;
  doc.setFont("helvetica", "bold");
  doc.text("Mode:", labelX, y);
  doc.setFont("helvetica", "normal");
  doc.text(safeText(mode), valueX, y);
  doc.setFont("helvetica", "bold");
  doc.text("Amount:", 160, y);
  doc.setFont("helvetica", "normal");
  doc.text(formatINR(amount), 190, y, { align: "right" });

  y += lineHeight;
  doc.setFont("helvetica", "bold");
  doc.text("Paid To:", labelX, y);
  doc.setFont("helvetica", "normal");
  doc.text(safeText(paidTo), valueX, y);
  doc.setFont("helvetica", "bold");
  doc.text("Status:", 160, y);
  doc.setFont("helvetica", "normal");
  doc.text(safeText(status), 190, y, { align: "right" });

  y += lineHeight * 2;
  doc.setFont("helvetica", "bold");
  doc.text("Particulars:", labelX, y);
  y += lineHeight;
  doc.setFont("helvetica", "normal");
  const wrapWidth = 170;
  const particularsLines = doc.splitTextToSize(particulars, wrapWidth);
  doc.text(particularsLines, left, y);
  y += particularsLines.length * (lineHeight * 0.9);

  y += 6;
  doc.setFont("helvetica", "bold");
  doc.text("Remarks:", labelX, y);
  y += lineHeight;
  doc.setFont("helvetica", "normal");
  const remarksLines = doc.splitTextToSize(remarks, wrapWidth);
  doc.text(remarksLines, left, y);
  y += remarksLines.length * (lineHeight * 0.9);

  y = Math.max(y + 20, 170);
  doc.setFont("helvetica", "normal");
  doc.text("Prepared By: _______________________", left, y);
  doc.text("Checked By: _______________________", 110, y);
  doc.text("Approved By: _______________________", 180, y);

  y += 14;
  doc.setFontSize(9);
  doc.text("This is a system-generated voucher.", 105, y, { align: "center" });
}

/* ---------- Public API (generatePDF / generateVoucher / generateAuto) ---------- */

const ReceiptDownload = {
  async generatePDF(receipt, opts = {}) {
    try {
      const safeReceipt = {
        ...receipt,
        amount: receipt.amount !== undefined && receipt.amount !== null ? Number(receipt.amount) : 0,
        amount_paid: receipt.amount_paid !== undefined && receipt.amount_paid !== null ? Number(receipt.amount_paid) : (receipt.amount !== undefined ? Number(receipt.amount) : 0),
        scholarship_amount: receipt.scholarship_amount !== undefined && receipt.scholarship_amount !== null ? Number(receipt.scholarship_amount) : 0,
        refund: receipt.refund !== undefined && receipt.refund !== null ? Number(receipt.refund) : 0,
        student_name: receipt.student_name ?? (opts && opts.student && (opts.student.name || opts.student.student_name)) ?? receipt.name,
        batch_name: receipt.batch_name ?? (opts && opts.student && opts.student.batch) ?? receipt.batch,
        course_year: receipt.course_year ?? (opts && opts.student && opts.student.course_year) ?? receipt.course_year
      };

      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageHeight = doc.internal.pageSize.getHeight();
      const halfHeight = pageHeight / 2;

      drawSingleReceipt(doc, safeReceipt, 0, "Student Copy", opts);
      drawSingleReceipt(doc, safeReceipt, halfHeight, "Office Copy", opts);

      const fileReceiptNo = String(getReceiptNo(safeReceipt)).replace(/\s+/g, "_") || "receipt";
      const blob = doc.output("blob");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `FeeReceipt_${fileReceiptNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      console.error("ReceiptDownload.generatePDF error:", err);
      throw err;
    }
  },

  async generateVoucher(row = {}, opts = {}) {
    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      drawVoucher(doc, { ...row, ...opts });
      const fileName = `Voucher_${String(row.voucher_no || row.receipt_no || row.receipt || row.id || "VCHR").replace(/\s+/g, "_")}.pdf`;
      const blob = doc.output("blob");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      console.error("ReceiptDownload.generateVoucher error:", err);
      throw err;
    }
  },

  async generateAuto(row = {}, opts = {}) {
    // convenience: choose voucher for refunds, receipt otherwise
    try {
      if (isRefundRow(row)) {
        await ReceiptDownload.generateVoucher(row, opts);
      } else {
        await ReceiptDownload.generatePDF(row, opts);
      }
    } catch (err) {
      throw err;
    }
  },

  isRefundRow
};

export default ReceiptDownload;